"use client";

import { Loader2, Send } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { MicIcon, type MicIconHandle } from "@/components/widget/icons/animated";
import { useIconHover } from "@/lib/use-icon-hover";
import { cn } from "@/lib/utils";

/**
 * Single-button surface that morphs between mic / send / stop. The
 * button stays in the same position so taps don't jump as the
 * draft fills up.
 */
export function MorphSendButton({
  kind,
  disabled,
  onSend,
  onMic,
  ariaLabels,
}: {
  kind: "mic" | "send";
  disabled: boolean;
  onSend: () => void;
  onMic: () => void;
  ariaLabels: { send: string; mic: string };
}) {
  const onClick = kind === "send" ? onSend : onMic;
  const ariaLabel = ariaLabels[kind];
  const micHover = useIconHover<MicIconHandle>();
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled && kind === "send"}
      aria-label={ariaLabel}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.9 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      {...(kind === "mic" ? micHover.parentHandlers : {})}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
        "bg-wx-primary text-wx-primary-fg hover:bg-wx-primary-hover",
        "disabled:opacity-50",
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        {/* Spinner takes priority over the send glyph — when the
            mutation is in flight, swap to `Loader2` so the visitor
            sees the request is being processed. */}
        {kind === "send" && disabled && (
          <motion.span
            key="sending"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.14 }}
          >
            <Loader2 size={14} className="animate-spin" />
          </motion.span>
        )}
        {kind === "send" && !disabled && (
          <motion.span
            key="send"
            initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.6, rotate: 30 }}
            transition={{ duration: 0.14 }}
          >
            <Send size={14} />
          </motion.span>
        )}
        {kind === "mic" && (
          <motion.span
            key="mic"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.14 }}
          >
            <MicIcon ref={micHover.iconRef} size={14} />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
